<template>
  <base-slide-over size="4xl" ref="slideOver" title="Create Prescription">
    <base-form ref="form" class="tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6" v-model="isFormValid">
      <div>Patient Information</div>
      <base-text-field label="Full Name" placeholder="Enter full name" v-model="fullName" class="tw-mt-3" required />
      <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-4">
        <base-email-field label="Email" placeholder="Enter email" v-model="email" required />
        <base-phone-number-input label="Phone" placeholder="Enter phone" v-model="mobile" :e164.sync="phone164" required />
      </div>
      <div :class="`tw-grid tw-grid-cols-1 sm:tw-grid-cols-${organization.collectDobSetting ? '2' : '1'} tw-gap-x-4`">
        <base-date-picker label="Date of Birth" placeholder="Enter date of birth" initial-view="year" v-model="dob" required v-if="organization.collectDobSetting" />
        <base-select label="Gender" :options="genderOptions" placeholder="Select gender" label-key="name" v-model="gender" required />
      </div>
      <div :class="`tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-4`">
        <base-date-picker label="Last Appointment" placeholder="Select last appointment date" v-model="lastAppointmentDate" required />
        <base-select
          label="Appointment Location"
          :options="appointmentLocationOptions"
          placeholder="Select the setting of the last appointment"
          label-key="name"
          v-model="appointmentLocation"
          required
        />
      </div>
      <base-textarea label="Known Allergies" placeholder="Enter known allergies" v-model="knownAllergies" />
      <div v-if="organization.collectMailingAddressSetting" class="tw-mt-7">
        <div>Mailing Address</div>
        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-x-4 tw-mt-3">
          <base-text-field class="tw-col-span-2" label="Line 1" placeholder="Enter line 1" v-model="mailingAddress.line1" />
          <base-text-field label="Line 2" placeholder="Enter line 2" v-model="mailingAddress.line2" />
        </div>
        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-x-4 tw-mt-5">
          <base-text-field label="City" placeholder="Enter city" v-model="mailingAddress.city" />
          <base-select label="State" placeholder="Select state" :options="STATE_OPTIONS" label-key="name" v-model="mailingAddress.state" />
          <base-text-field label="Zip Code" placeholder="Enter zip code" v-model="mailingAddress.postalCode" />
        </div>
      </div>
      <base-select
        label="Practice Location"
        placeholder="Select a location"
        :optgroups="locationOptGroups"
        v-model="selectedLocationId"
        label-key="name"
        value-key="businessLocationId"
        required
        v-if="locations.length > 1"
        class="tw-mt-6"
      />
      <div class="tw-mt-2 tw-mb-3">Prescription Programs</div>
      <prescription-input
        v-for="prescriptionIndex in prescriptions.length"
        :key="prescriptionIndex"
        :programs="sortedPrograms"
        v-model="prescriptions[prescriptionIndex - 1]"
        :class="['tw-mb-1', { 'tw-border-t tw-pt-6 tw-mb-6': prescriptionIndex > 1 }]"
        :required="!prescriptions.find((p) => !!p.businessProgramId) && prescriptionIndex === 1"
      />
      <div class="tw-flex tw-justify-center tw-py-2">
        <base-button @click="addAnotherProgram" type="flat">Add Another Program</base-button>
      </div>
    </base-form>
    <template #fixed-bottom>
      <div class="tw-py-2 tw-px-3 tw-flex tw-justify-end">
        <base-button type="flat" @click="$refs.slideOver.hide()" class="tw-mr-3">Cancel</base-button>
        <base-button @click="createPrescription" :disabled="!isFormValid" :loading="isCreating">Create</base-button>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import PrescriptionInput from "@/views/business/components/PrescriptionInput";
import { orderBy } from "lodash";
import { STATE_OPTIONS } from "@/constants/constants";
import { mapGetters } from "vuex";

export default {
  components: {
    PrescriptionInput,
  },
  props: {
    businessId: String,
  },
  data() {
    return {
      isCreating: false,
      isFormValid: true,
      fullName: "",
      email: "",
      mobile: "",
      phone164: "",
      dob: "",
      gender: null,
      genderOptions: [{ value: "male", name: "Male" }, { value: "female", name: "Female" }],
      appointmentLocation: null,
      appointmentLocationOptions: [{ value: "office", name: "In Office Visit" }, { value: "virtual", name: "Virtual Visit" }],
      lastAppointmentDate: null,
      knownAllergies: "",
      STATE_OPTIONS,
      mailingAddress: {
        line1: null,
        line2: null,
        city: null,
        state: null,
        postalCode: null,
      },
      selectedLocationId: null,
      prescriptions: [{ businessProgramId: null, medications: [], notes: null }],
      programs: [],
      programGroups: [],
    };
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    ...mapGetters("location", ["location", "locations"]),
    locationOptGroups() {
      return STATE_OPTIONS.filter((s) => this.locations.find((l) => l.state === s.value)).map((s) => ({
        label: s.value,
        options: orderBy(this.locations.filter((l) => l.state === s.value), ["name"]),
      }));
    },
    sortedPrograms() {
      if (!this.programGroups.length) return [];
      // test
      return orderBy(
        this.programGroups
          .filter((pg) => this.programs.some((p) => p.programGroup === pg))
          .map((pg) => ({
            label: pg ? pg : "No Group",
            options: orderBy(this.programs.filter((p) => p.programGroup === pg), ["name"]),
          })),
        ["label"],
      );
    },
  },
  created() {
    this.getProgramGroups();
    this.getPrograms();
  },
  methods: {
    show() {
      this.$refs.slideOver.show();
      this.$refs.form.reset();
      this.prescriptions = [{ businessProgramId: null, medications: [], notes: null }];
    },
    hide() {
      this.$refs.slideOver.hide();
    },
    async getPrograms() {
      try {
        const response = await this.$axios.get(`/businesses/${this.businessId}/programs/list?includeDeleted=false`);
        this.programs = response.data;
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to retrieve programs",
          message: "We were not able to retrieve your business programs. There may have been a connection error. Please reload the page to try again.",
          error,
        });
      }
    },
    async getProgramGroups() {
      try {
        const response = await this.$axios.get(`/businesses/${this.businessId}/programs/groups`);
        this.programGroups = response.data;
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to retrieve program groups",
          message: "We were not able to retrieve your business program groups. There may have been a connection error. Please reload the page to try again.",
          error,
        });
      }
    },
    async createPrescription() {
      if (!this.$refs.form.validate()) return;

      try {
        this.isCreating = true;
        await this.$axios.post(`/businesses/${this.businessId}/prescriptions`, {
          businessLocationId: this.selectedLocationId,
          user: {
            fullName: this.fullName,
            email: this.email,
            mobilePhone: this.phone164,
            mailingAddressLine1: this.mailingAddress.line1,
            mailingAddressLine2: this.mailingAddress.line2,
            mailingAddressCity: this.mailingAddress.city,
            mailingAddressState: this.mailingAddress.state,
            mailingAddressPostalCode: this.mailingAddress.postalCode,
            dob: this.dob,
            gender: this.gender,
            knownAllergies: this.knownAllergies,
            lastAppointmentDate: this.lastAppointmentDate,
            appointmentLocation: this.appointmentLocation,
          },
          prescriptions: this.prescriptions
            .filter((p) => !!p.businessProgramId)
            .map((p) => ({
              ...p,
              businessLocationId: this.selectedLocationId,
              medications: p.medications
                .filter((m) => !!m)
                .map((m, i) => ({
                  businessItemCategoryId: m,
                  sortOrder: i,
                })),
              notes: [p.notes],
            })),
        });
        this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Prescription Created", message: "The prescription has been submitted successfully." });
        this.hide();
        this.$emit("created");
      } catch (error) {
        if (error.response.data[0].message == "User already has prescriptions for this program") {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Patient already has active plan for this program",
            message: `If you are attempting refill a prescription for this patient please goto Activities to manage your refill authorizations.`,
            error,
          });
        } else {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to sign up",
            message: `We were not able to sign up ${this.fullName}. Please try again.`,
            error,
          });
        }

        throw error;
      } finally {
        this.isCreating = false;
      }
    },
    addAnotherProgram() {
      this.prescriptions.push({
        businessProgramId: null,
        medications: [],
        notes: null,
      });
    },
  },
  watch: {
    locations: {
      immediate: true,
      handler() {
        if (this.locations.length === 1) this.selectedLocationId = this.locations[0].businessLocationId;
      },
    },
  },
};
</script>
